import { createSlice } from "@reduxjs/toolkit";
import { getUploadManager } from "./uploadManagerSingleton";

export const UPLOAD_SLICE_KEY = "uploadTransfers";
const TERMINAL_STATUSES = new Set(["complete", "cancelled"]);

const initialState = {
  jobs: {},
  activeJobIds: [],
  hydrated: false
};

function isActive(job) {
  return Boolean(job && job.id && !TERMINAL_STATUSES.has(job.status));
}

function upsertJob(state, job) {
  if (!job || !job.id) return;
  state.jobs[job.id] = job;
  if (isActive(job)) {
    if (!state.activeJobIds.includes(job.id)) state.activeJobIds.push(job.id);
  } else {
    state.activeJobIds = state.activeJobIds.filter(id => id !== job.id);
  }
}

const uploadSlice = createSlice({
  name: UPLOAD_SLICE_KEY,
  initialState,
  reducers: {
    hydrateUploadsSuccess(state, { payload }) {
      const jobs = (payload || []).filter(job => job && job.id);
      state.jobs = jobs.reduce((acc, job) => {
        acc[job.id] = job;
        return acc;
      }, {});
      state.activeJobIds = jobs.filter(isActive).map(job => job.id);
      state.hydrated = true;
    },
    registerUpload(state, { payload }) {
      upsertJob(state, payload);
    },
    updateUpload(state, { payload }) {
      upsertJob(state, payload);
    },
    removeUpload(state, { payload }) {
      const jobId =
        typeof payload === "string" ? payload : payload && payload.jobId;
      if (!jobId) return;
      delete state.jobs[jobId];
      state.activeJobIds = state.activeJobIds.filter(id => id !== jobId);
    }
  }
});

export const {
  hydrateUploadsSuccess,
  registerUpload,
  updateUpload,
  removeUpload
} = uploadSlice.actions;

export const hydrateUploads = ({ manager } = {}) => async dispatch => {
  const uploadManager = manager || (await getUploadManager());
  if (
    !uploadManager ||
    typeof uploadManager.listIncompleteUploads !== "function"
  ) {
    throw new Error(
      "An upload manager with listIncompleteUploads is required."
    );
  }
  const jobs = (await uploadManager.listIncompleteUploads()) || [];
  dispatch(hydrateUploadsSuccess(jobs));
  return jobs;
};

export const selectUploadState = state =>
  state[UPLOAD_SLICE_KEY] || initialState;
export const selectUploads = state => selectUploadState(state).jobs || {};
export const selectUploadById = (state, jobId) =>
  selectUploads(state)[jobId] || null;
export const selectActiveUploads = state => {
  const uploadState = selectUploadState(state);
  return uploadState.activeJobIds
    .map(jobId => uploadState.jobs[jobId])
    .filter(isActive);
};
export const selectUploadHydrated = state =>
  Boolean(selectUploadState(state).hydrated);

export const pauseUploadJob = ({ jobId, manager } = {}) => async dispatch => {
  const uploadManager = manager || (await getUploadManager());
  const job = await uploadManager.pauseUpload({ jobId });
  if (job) dispatch(updateUpload(job));
  return job;
};

export const resumeUploadJob = ({ jobId, manager } = {}) => async (
  dispatch,
  getState
) => {
  const uploadManager = manager || (await getUploadManager());
  const result = uploadManager.resumeUpload({ jobId });
  const job = selectUploadById(getState(), jobId);
  if (result && job) {
    dispatch(
      updateUpload({
        ...job,
        status: "running",
        updatedAt: new Date().toISOString()
      })
    );
  }
  return result;
};

export const cancelUploadJob = ({ jobId, manager } = {}) => async dispatch => {
  const uploadManager = manager || (await getUploadManager());
  const job = await uploadManager.cancelUpload({ jobId });
  if (job) dispatch(updateUpload(job));
  return job;
};

export default uploadSlice.reducer;
